/**
 * NEW NOTE COMPONENT
 *
 * Modal component for creating new notes with entity association functionality.
 *
 * Key Features:
 * - Modal-based note creation interface with NoteEditor integration
 * - Entity selection: Manual search via EntityTypeahead or automatic pre-selection
 * - Auto-selection: Supports pre-selecting contacts/accounts from AI chat function calls
 * - Suggested contact support: Handles fake IDs (99000000+) for RLP persons not yet in CRM
 * - Visual search distinction: Existing vs suggested contacts clearly distinguished in dropdown
 * - Entity priority: ContactId takes precedence over accountId when both are provided
 * - Google integrations: Calendar event creation and Gmail composition after note creation
 * - Form validation: Ensures entity selection before allowing note submission
 * - Success flow: Shows success screen with Google integration options after creation
 * - Team association: Properly extracts and passes teamId from entity data to NoteEditor
 *
 * Auto-Selection Logic:
 * - When preSelectedContactId is provided: Fetches and auto-selects that contact
 * - When preSelectedAccountId is provided (no contactId): Fetches and auto-selects that account
 * - Suggested contacts: Detects fake IDs (>= 99000000), extracts personId, links via PUT_CONTACT
 * - Manual selection: User can search and select entities via typeahead when no pre-selection
 * - Error handling: Falls back to manual selection if entity fetching fails
 * - Team extraction: Captures teamId from fetched contact/account for proper team filtering
 *
 * Event Bus Integration:
 * - Opened via OPEN_NOTE_MODAL event from AI chat service user actions
 * - Receives contactId/accountId from AI function calls for auto-selection
 * - Integrates with App.tsx and Navbar.tsx for seamless modal management
 *
 * Suggested Contact Flow (FIXED ✅):
 * - AI chat searches for contacts via SearchAll API with fake ID generation
 * - SearchAll SQL returns fake IDs (99000000 + personId) for suggested contacts
 * - Contact IDs from search results can be used to prefill the note contact target
 * - fetchContactById detects fake ID and extracts personId (contactId - 99000000)
 * - Links RLP person to create Contact record via PUT_CONTACT endpoint
 * - Fetches newly created contact data for note association
 * - Contact name displays correctly in note creation interface
 *
 * Team Selection Fix (FIXED ✅):
 * - Properly extracts teamId from contact/account entity data
 * - Passes entityTeamId to NoteEditor for proper team filtering
 * - Ensures note creation via AI chat shows only associated team (like manual creation)
 * - Fixed issue where AI-created notes showed all teams instead of contact's team
 *
 * Props:
 * - isModalOpen: Controls modal visibility
 * - toggleModal: Function to close the modal
 * - standalone: Optional flag for standalone usage
 * - preSelectedContactId: Auto-select contact by ID (priority over account)
 * - preSelectedAccountId: Auto-select account by ID (when no contact specified)
 * - preSelectedTeamId: Optional team seed for callers that already know the workflow team
 * - initialContent: Optional note draft seed
 *
 * Changes:
 * - Stabilizes new-note props while shared editor resolves visibility and preserves draft edits
 * - Preserves follow-up tasks and export actions for a creator's saved note regardless of its selected level
 * - Resolves note ownership and task permissions from the authenticated TeamContext user
 * - Uses a responsive 3:2 New Note form with entity and note content in the primary section,
 *   type, flags, and audience controls in the details section, plus a shared popup footer
 * - Added teamId extraction in loadPreSelectedEntity for both contacts and accounts
 * - Ensures selectedEntity includes teamId from fetched entity data
 * - Matches behavior of manual note creation from contact detail pages
 * - Stores a composed saved-note snapshot after note creation so success actions use real note data
 * - Adds a conditional Add Task action on the success screen for task-flagged notes and creates persisted tasks
 * - Aligns the note-linked task dialog chrome with the shared New Task modal
 * - Routes suggested-contact linking through the canonical contact cache invalidation path
 * - Keeps the shared desktop dialog at the 620px initial-form height, capped by the viewport, after entity selection
 * - Sends assignment-email opt-in with task creation and displays backend queue outcomes
 * - Persists the priority selected when creating a task from a newly saved note
 * - Hosts note-linked task creation in the shared wide responsive task-popup shell
 * - Aligns the New Note shell, two-section spacing, and title with the shared popup system
 * - Groups Account or Contact with joined note title/body fields for compact vertical spacing
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Note, NOTE_FLAGS, createBlankNote } from '../../models/Note';
import { THEME_CLASSES } from '../../constants/themeConstants';
import NoteEditor from './NoteEditor';
import NoteTaskPrototypePanel from './NoteTaskPrototypePanel';
import {
  TASK_POPUP_CLOSE_CLASS_NAME,
  TASK_POPUP_HEADER_CLASS_NAME,
  TASK_POPUP_TITLE_CLASS_NAME,
} from '../task/popup';
import {
  POPUP_CLOSE_CLASS_NAME,
  POPUP_HEADER_CLASS_NAME,
  POPUP_LABEL_CLASS_NAME,
  POPUP_PANEL_CLASS_NAME,
  POPUP_TITLE_CLASS_NAME,
} from '../popup';
import { request } from '../../services/dataService';
import { API_ENDPOINTS } from '../../constants/apiEndpoints';
import { createTask } from '../../services/taskService';
import { showTaskNotificationOutcome } from '../task/taskNotificationToast';
import EntityTypeahead, { EntityTypeaheadResult } from '../common/EntityTypeahead';
import { openNoteInCalendar, openNoteInGmail } from '../../utils/googleUtils';
import { EVENTS } from '../../constants/eventConstants';
import { useEventBus } from '../../context/EventBusContext';
import { toast } from 'react-toastify';
import { Button, DialogShell } from '../primitives';
import type { TaskMutationPayload } from '../../models/Task';
import type { NoteTaskFormValues } from './NoteTaskPrototypePanel';
import { useLinkContactMutation } from '../../query/contactQueries';
import { useTeams } from '../../context/TeamContext';

interface NewNoteProps {
  isModalOpen: boolean;
  toggleModal: () => void;
  standalone?: boolean;
  preSelectedContactId?: number;
  preSelectedAccountId?: number;
  preSelectedTeamId?: number;
  initialContent?: string;
}

interface SelectedEntity {
  id: number;
  personId: number;
  name: string;
  type: string;
  email?: string;
  teamId?: number;
  accountId?: number | null;
}

const NewNote: React.FC<NewNoteProps> = ({
  isModalOpen,
  toggleModal,
  standalone = false,
  preSelectedContactId,
  preSelectedAccountId,
  preSelectedTeamId,
  initialContent
}) => {
  const { currentUserId } = useTeams();
  const { publish } = useEventBus();
  const linkContact = useLinkContactMutation();
  const [selectedEntity, setSelectedEntity] = useState<SelectedEntity | null>(null);
  const [savedNote, setSavedNote] = useState<Note | null>(null);
  const [savedNoteReadable, setSavedNoteReadable] = useState(true);
  const [taskPrototypeOpen, setTaskPrototypeOpen] = useState(false);
  const [taskPrototypeCaptured, setTaskPrototypeCaptured] = useState(false);
  const [submissionState, setSubmissionState] = useState<'idle' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isModalOpen) {
      setSelectedEntity(null);
      setSavedNote(null);
      setSavedNoteReadable(true);
      setTaskPrototypeOpen(false);
      setTaskPrototypeCaptured(false);
      setSubmissionState('idle');
      setError(null);

      // Handle pre-selected entities
      if (preSelectedContactId !== undefined || preSelectedAccountId !== undefined) {
        loadPreSelectedEntity();
      }
    }
  }, [isModalOpen, preSelectedContactId, preSelectedAccountId, preSelectedTeamId, initialContent]);

  // Helper function to load pre-selected entity
  const loadPreSelectedEntity = async () => {
    try {
      let entityData = null;

      // Prioritize contact over account
      if (preSelectedContactId !== undefined) {
        entityData = await fetchContactById(preSelectedContactId);
        if (entityData) {
          setSelectedEntity({
            id: entityData.id,
            personId: entityData.personId,
            name: `${entityData.firstName} ${entityData.lastName}`.trim(),
            type: 'Contact',
            email: entityData.email,
            teamId: entityData.teamId,
            accountId: entityData.accountId ?? null,
          });
        }
      } else if (preSelectedAccountId !== undefined) {
        entityData = await fetchAccountById(preSelectedAccountId);
        if (entityData) {
          // Try multiple possible name fields
          const accountName = entityData.accountName || entityData.name || entityData.companyName || `Account ${entityData.id}`;

          setSelectedEntity({
            id: entityData.id || 0,
            personId: entityData.personId || 0,
            name: accountName,
            type: 'Account',
            email: entityData.email || undefined,
            teamId: entityData.teamId,
            accountId: entityData.id || 0,
          });
        }
      }
    } catch (error) {
      console.error('Failed to load pre-selected entity:', error);
      // Don't show error to user, just proceed without pre-selection
    }
  };

  // Helper function to fetch contact by ID
  const fetchContactById = async (contactId: number) => {
    try {
      // Handle suggested contacts (fake IDs starting with 99000000)
      if (contactId >= 99000000) {
        // Extract personId from fake ID
        const personId = contactId - 99000000;

        // Link/create the contact using the personId
        const linkResponse = await linkContact.mutateAsync({ personId });
        if (linkResponse && linkResponse.id) {
          return await request(API_ENDPOINTS.GET_CONTACT, { id: linkResponse.id });
        }
        return null;
      }

      const response = await request(API_ENDPOINTS.GET_CONTACT, { id: contactId });
      return response;
    } catch (error) {
      console.error('Failed to fetch contact:', error);
      return null;
    }
  };

  // Helper function to fetch account by ID
  const fetchAccountById = async (accountId: number) => {
    try {
      const response = await request(API_ENDPOINTS.GET_ACCOUNT, { id: accountId });

      // If the response is minimal, try the accounts list endpoint as fallback
      if (response && Object.keys(response).length <= 2) {
        try {
          const accountsResponse = await request(API_ENDPOINTS.GET_ACCOUNTS);

          // Find the account with matching ID
          const fullAccount = accountsResponse?.find((acc: any) => acc.id === accountId);
          if (fullAccount) {
            return fullAccount;
          }
        } catch (fallbackError) {
          console.error('Fallback accounts list failed:', fallbackError);
        }
      }

      return response;
    } catch (error) {
      console.error('Failed to fetch account:', error);
      return null;
    }
  };

  const handleClose = () => {
    toggleModal();
  };

  const handleEntitySelect = (result: EntityTypeaheadResult): void => {
    setSelectedEntity({
      id: result.id,
      personId: result.personId || 0,
      name: result.name || '',
      type: result.type,
      email: result.email,
      teamId: result.teamId,
      accountId: result.type === 'Account' ? result.id : (result.accountId ?? null),
    });
    setError(null);
  };

  const removeSelectedEntity = (): void => {
    setSelectedEntity(null);
  };

  const handleOpenCalendar = (note: Note) => {
    // Default start time to now rounded to nearest hour
    const startDate = new Date();
    startDate.setMinutes(0, 0, 0);
    startDate.setHours(startDate.getHours() + 1);

    openNoteInCalendar(note, startDate);
  };

  const handleOpenGmail = (note: Note) => {
    openNoteInGmail(note, selectedEntity?.email || '');
  };

  const blankNote = useMemo(() => {
    const objectType = selectedEntity?.type === 'Account' ? 'account' : 'contact';
    const blankNote = createBlankNote(objectType);
    const effectiveTeamId = selectedEntity?.teamId ?? preSelectedTeamId;

    blankNote.objectIds = selectedEntity?.id?.toString() || (selectedEntity?.personId ? 99000000 + selectedEntity?.personId : null)?.toString() || ''
    blankNote.createdBy = currentUserId || 0;
    blankNote.updatedBy = currentUserId || 0;
    blankNote.flags = 0;
    blankNote.content = initialContent?.trim() || '';
    blankNote.teamIds = effectiveTeamId ? String(effectiveTeamId) : '';
    blankNote.accountName = selectedEntity?.type === 'Account' ? selectedEntity.name : undefined;
    blankNote.contactName = selectedEntity?.type === 'Contact' ? selectedEntity.name : undefined;
    return blankNote;
  }, [selectedEntity, preSelectedTeamId, currentUserId, initialContent]);

  const resolveCreatedNoteId = (noteResponse?: { noteId?: number; id?: number } | Array<{ noteId?: number; id?: number }>) => {
    const normalizedResponse = Array.isArray(noteResponse) ? noteResponse[0] : noteResponse;
    return normalizedResponse?.noteId ?? normalizedResponse?.id ?? null;
  };

  const buildSavedNoteSnapshot = (note: Note, noteResponse?: { noteId?: number; id?: number } | Array<{ noteId?: number; id?: number }>) => ({
    ...note,
    id: resolveCreatedNoteId(noteResponse) ?? note.id,
    accountName: selectedEntity?.type === 'Account' ? selectedEntity.name : note.accountName,
    contactName: selectedEntity?.type === 'Contact' ? selectedEntity.name : note.contactName,
  });

  const handleOpenTaskPrototype = () => {
    if (!savedNoteReadable) return;
    setTaskPrototypeCaptured(false);
    setTaskPrototypeOpen(true);
  };

  const handleCloseTaskPrototype = () => {
    setTaskPrototypeOpen(false);
  };

  const handleSkipTaskPrototype = async () => {
    if (!savedNote || !savedNoteReadable) {
      handleCloseTaskPrototype();
      return;
    }

    const noteWithoutTaskFlag = NOTE_FLAGS.clearFlag(savedNote, NOTE_FLAGS.TASKS);

    try {
      const { id, ...noteDataWithoutId } = noteWithoutTaskFlag;
      await request(API_ENDPOINTS.PUT_NOTE, { id, ...noteDataWithoutId });
      setSavedNote(noteWithoutTaskFlag);
      setTaskPrototypeCaptured(false);
      setTaskPrototypeOpen(false);
      setError(null);
      toast.info('Tasks unchecked for this note.');
    } catch (error) {
      console.error('Failed to clear Tasks for note:', error);
      setError('Failed to update the note. Please try again.');
    }
  };

  const resolveTaskAccountId = async (): Promise<number | null> => {
    if (selectedEntity?.type === 'Account') {
      return selectedEntity.id;
    }

    if (selectedEntity?.type === 'Contact') {
      if (selectedEntity.accountId) {
        return selectedEntity.accountId;
      }

      try {
        const contact = await request(API_ENDPOINTS.GET_CONTACT, { id: selectedEntity.id });
        return contact?.accountId ?? null;
      } catch (loadError) {
        console.error('Failed to resolve contact account for task creation:', loadError);
        return null;
      }
    }

    return savedNote?.accountId ?? null;
  };

  const handleCreateTask = async (taskValues: NoteTaskFormValues) => {
    if (!savedNote || !savedNoteReadable) {
      return;
    }

    try {
      const accountId = await resolveTaskAccountId();
      if (!accountId) {
        toast.error('Unable to determine an account for this task.');
        return;
      }

      const taskPayload: TaskMutationPayload = {
        title: taskValues.title,
        description: taskValues.description.trim(),
        accountId,
        contactId: selectedEntity?.type === 'Contact' ? selectedEntity.id : null,
        sourceNoteId: savedNote.id,
        dueDate: taskValues.dueDate || null,
        priority: taskValues.priority,
        sourceType: 'Note',
        assigneeIds: taskValues.assigneeIds,
        sendEmail: taskValues.sendEmail,
      };

      const creationResult = await createTask(taskPayload);
      const savedTask = creationResult.task;

      showTaskNotificationOutcome(creationResult.notification);

      setTaskPrototypeOpen(false);
      setTaskPrototypeCaptured(true);
      toast.success(`Task "${savedTask.title}" created.`);
    } catch (error) {
      console.error('Failed to create task from new note:', error);
      toast.error('Failed to create task.');
      throw error;
    }
  };

  const renderSuccessView = () => {
    if (!savedNote) {
      return null;
    }

    const showTaskAction = savedNoteReadable && NOTE_FLAGS.hasFlag(savedNote, NOTE_FLAGS.TASKS);
    const successActionButtonClassName = `inline-flex items-center px-3 py-1 text-xs rounded-md border ${THEME_CLASSES.border} ${THEME_CLASSES.text.primary} transition-all duration-200 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-md hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`;

    return (
      <div className="flex-1 flex flex-col h-full">
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-2xl text-center">
            <div className={`mb-6 ${THEME_CLASSES.text.secondary}`}>
              Your note has been created successfully.
              {!savedNoteReadable && <p className="mt-2 text-xs">The selected level is above your access. You can no longer view this note or create tasks from it.</p>}
            </div>
            {savedNoteReadable && <div className="flex flex-wrap gap-4 justify-center mb-6">
              {showTaskAction && (
                <button
                  onClick={handleOpenTaskPrototype}
                  className={successActionButtonClassName}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                  Add Task
                </button>
              )}
              <button
                onClick={() => handleOpenCalendar(savedNote)}
                className={successActionButtonClassName}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                </svg>
                Add to Google Calendar
              </button>
              <button
                onClick={() => handleOpenGmail(savedNote)}
                className={successActionButtonClassName}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
                Send via Gmail
              </button>
            </div>}
            {taskPrototypeCaptured && !taskPrototypeOpen && (
              <div className={`mb-6 text-sm ${THEME_CLASSES.status.success} rounded-md px-4 py-3`}>
                Task created from this note.
              </div>
            )}
            <DialogShell
              open={showTaskAction && taskPrototypeOpen}
              title="New task"
              onClose={handleCloseTaskPrototype}
              panelClassName="!w-[calc(100vw-1.5rem)] !max-w-5xl !rounded-2xl sm:!w-[calc(100vw-3rem)]"
              headerClassName={TASK_POPUP_HEADER_CLASS_NAME}
              titleClassName={TASK_POPUP_TITLE_CLASS_NAME}
              closeButtonClassName={TASK_POPUP_CLOSE_CLASS_NAME}
              bodyPadding={false}
            >
              <NoteTaskPrototypePanel
                savedNote={savedNote}
                selectedEntity={selectedEntity}
                open={showTaskAction && taskPrototypeOpen}
                onClose={handleCloseTaskPrototype}
                onSkipTask={handleSkipTaskPrototype}
                onSubmitTask={handleCreateTask}
                submitLabel="Create Task"
              />
            </DialogShell>
          </div>
        </div>
        <div className={`${THEME_CLASSES.border} border-t`}>
          <div className="flex items-center justify-end gap-4 py-4 px-6">
            <button
              onClick={handleClose}
              className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-xs font-medium ${THEME_CLASSES.button.primary}`}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderAssociationField = () => (
    <div className="min-w-0" data-testid="note-association-control">
      <label className={POPUP_LABEL_CLASS_NAME}>Account or Contact</label>
      <div className="relative">
        <EntityTypeahead
          placeholder="Search accounts and contacts..."
          onSelect={handleEntitySelect}
          className={`w-full ${selectedEntity ? 'hidden' : ''}`}
        />
        {selectedEntity && (
          <div className="flex min-h-10 items-center gap-2">
            <div className={`
              flex min-w-0 items-center rounded-lg px-3 py-2 text-sm
              ${selectedEntity.type === 'Account'
                ? 'bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-200'
                : 'bg-purple-100 text-purple-800 dark:bg-purple-950/60 dark:text-purple-200'}
            `}>
              <span className="truncate">{selectedEntity.name}</span>
              <Button
                onClick={removeSelectedEntity}
                variant="icon"
                className="ml-2 h-6 w-6 rounded-full bg-transparent hover:bg-transparent"
                aria-label={`Remove ${selectedEntity.name}`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </Button>
            </div>
          </div>
        )}
      </div>
      {!selectedEntity && error === 'Please select at least one contact or account' && (
        <div className="mt-1 text-xs text-red-500">{error}</div>
      )}
    </div>
  );

  const renderForm = () => (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="grid flex-1 grid-cols-1 gap-y-5 px-5 pt-5 sm:px-6 sm:pt-6 lg:grid-cols-[minmax(0,3fr)_minmax(19rem,2fr)] lg:grid-rows-[auto_minmax(20rem,1fr)_auto] lg:gap-x-8">
        <NoteEditor
          note={blankNote}
          entityTeamId={selectedEntity?.teamId ?? preSelectedTeamId}
          layout="new-note-dialog"
          associationField={renderAssociationField()}
          onSave={async (updatedNote, saveContext) => {
            if (standalone) {
              return;
            }

            if (!selectedEntity) {
              setError('Please select at least one contact or account');
              return;
            }

            try {
              const noteResponse = await request(API_ENDPOINTS.POST_NOTE, updatedNote);
              const savedNoteSnapshot = buildSavedNoteSnapshot(updatedNote, noteResponse);

              if (!savedNoteSnapshot.id) {
                throw new Error('Created note response did not include a note ID');
              }

              publish(EVENTS.NOTES_UPDATED, {
                noteId: savedNoteSnapshot.id,
                accountId: selectedEntity?.type === 'Account'
                  ? selectedEntity.id
                  : (selectedEntity?.accountId ?? undefined),
                contactId: selectedEntity?.type === 'Contact'
                  ? selectedEntity.id
                  : undefined,
              });

              setSavedNote(savedNoteSnapshot);
              const canReadAfterSave = saveContext?.canReadAfterSave ?? true;
              setSavedNoteReadable(canReadAfterSave);
              setTaskPrototypeCaptured(false);
              setTaskPrototypeOpen(canReadAfterSave && NOTE_FLAGS.hasFlag(updatedNote, NOTE_FLAGS.TASKS));
              setSubmissionState('success');
              setError(null);
            } catch (err) {
              console.error('Failed to create note:', err);
              setError('Failed to create note. Please try again.');
              setSubmissionState('error');
            }
          }}
          onCancel={handleClose}
          currentUserId={currentUserId ?? undefined}
          submitButtonText="Create Note"
        />
      </div>
      {error && error !== 'Please select at least one contact or account' && (
        <div className={`${THEME_CLASSES.border} border-t px-6 py-2 text-red-600 text-sm`}>
          {error}
        </div>
      )}
    </div>
  );

  return (
    <DialogShell
      open={isModalOpen}
      title="New note"
      onClose={handleClose}
      panelClassName={`${POPUP_PANEL_CLASS_NAME} flex flex-col lg:min-h-[min(38.75rem,90vh)]`}
      headerClassName={POPUP_HEADER_CLASS_NAME}
      titleClassName={POPUP_TITLE_CLASS_NAME}
      closeButtonClassName={POPUP_CLOSE_CLASS_NAME}
      bodyClassName="flex-1 flex flex-col overflow-auto p-0"
      bodyPadding={false}
    >
      {submissionState === 'success' ? renderSuccessView() : renderForm()}
    </DialogShell>
  );
};

export default NewNote;
